/** Shared chrome for detail-page panels — the Job, Part Type, User and Shipment
 *  pages all draw their field grids, section labels, buttons and toggles from
 *  here, so a panel added to any of them lines up with the rest rather than
 *  inventing its own spacing.
 *
 *  These tokens started life in `pages/Users/UpdateUser/userPanelStyles.ts`.
 *  They moved here when the Shipment detail pages needed them: a page must not
 *  import from another page, and a second copy is how the two toggles diverged
 *  the last time. `userPanelStyles.ts` now re-exports from this file.
 *
 *  Sits alongside `tableStyles.ts`, which does the same job for V4 tables. */
export const panelFieldGridSx = {
  display: 'grid',
  gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
  gap: 1.5,
};

export const sectionLabelSx = {
  fontSize: '11px',
  fontWeight: 600,
  color: 'text.secondary',
  textTransform: 'uppercase' as const,
  letterSpacing: '0.04em',
};

export const outlinedBtnSx = {
  py: '5px',
  px: '12px',
  textTransform: 'none' as const,
  fontWeight: 600,
  fontSize: '11px',
  // Matches the summary-bar Save; the `Button` atom would otherwise impose 8px.
  borderRadius: '4px',
  minWidth: 0,
  borderColor: 'v4.borderDark',
  color: 'text.primary',
  '&:hover': { borderColor: 'v4.accent', color: 'v4.accentDark', bgcolor: 'transparent' },
};

/** A destructive secondary action — Cancel, Discard, Archive. Same metrics as
 *  `outlinedBtnSx` so it lines up in a row with the others; only the colour
 *  differs, because the shape is what makes a button row read as one set. */
export const dangerBtnSx = {
  ...outlinedBtnSx,
  color: 'error.main',
  borderColor: 'error.light',
  '&:hover': { borderColor: 'error.main', color: 'error.dark', bgcolor: 'transparent' },
};

/** A destructive icon action inside a table row — remove, delete, archive.
 *  Quiet until hovered, so a long list isn't a column of red. */
export const rowIconBtnSx = {
  p: 0.5,
  color: 'text.secondary',
  '&:hover': { color: 'error.main', bgcolor: 'transparent' },
};

/** Enterprise toggle, applied as `'& .MuiSwitch-root': toggleSx` from a wrapper
 *  — the shared `Switch` atom takes no `sx`, and the nesting also lifts these to
 *  0,3,0 so they outrank MUI's own `.MuiSwitch-sizeSmall .MuiSwitch-*` rules
 *  (as flat siblings they tie and lose on injection order).
 *
 *  Track fill, thumb fill and elevation are all stated rather than partially
 *  overridden: MUI's small switch is oversized next to 11–13px labels, and once
 *  the thumb's elevation is dropped a white thumb has no edge against the grey
 *  track, so the control reads as one solid blob instead of a toggle. */
export const toggleSx = {
  width: 34,
  height: 20,
  padding: 0,
  display: 'flex',
  '& .MuiSwitch-switchBase': {
    padding: '3px',
    color: 'common.white',
    '&.Mui-checked': {
      transform: 'translateX(14px)',
      color: 'common.white',
      // Teal only ever fills here — it never carries text, so `v4.accent` is fine.
      '& + .MuiSwitch-track': { bgcolor: 'v4.accent', opacity: 1 },
    },
  },
  // `boxShadow: 1` is the theme's elevation token, not a hardcoded shadow.
  '& .MuiSwitch-thumb': { width: 14, height: 14, boxShadow: 1 },
  '& .MuiSwitch-track': { borderRadius: 10, bgcolor: 'v4.borderDark', opacity: 1 },
};

/** Apply to any wrapper holding a labelled toggle. Bundles the switch chrome
 *  with the label spacing rather than exporting them separately: MUI's
 *  `FormControlLabel` margins are calibrated for a ripple-padded Checkbox, so
 *  against `toggleSx`'s zero-padding switch the label ends up flush against the
 *  track. Keeping them in one token means a caller can't take the chrome and
 *  forget the spacing — which is exactly how the two toggles diverged before. */
export const toggleFieldSx = {
  '& .MuiSwitch-root': toggleSx,
  '& .MuiFormControlLabel-root': { marginLeft: 0, gap: '8px' },
};

/** The shared `Button` atom only offers contained/outlined MUI variants, so the
 *  contained one is flattened here rather than adding a variant to an atom with
 *  ~200 consumers. */
export const ghostBtnSx = {
  ...outlinedBtnSx,
  border: 1,
  boxShadow: 'none',
  bgcolor: 'transparent',
  // UploadButton lays its icon and label out in a gapless Stack.
  '& .MuiStack-root': { gap: '6px' },
  '&:hover': {
    boxShadow: 'none',
    bgcolor: 'transparent',
    borderColor: 'v4.accent',
    color: 'v4.accentDark',
  },
};

// Re-exported, not restated: every Save on a detail page has to match the
// summary-bar Save, and two copies of the same token is how they drifted apart
// before.
export { accentBtnSx } from '../components/molecules/UserSummaryBar/UserSummaryBar.styles';
